/**
 * The thread, paginated backwards.
 *
 * The endpoint answers newest-first (which is what "page 1 = what you see" means for a
 * chat), while the list renders oldest-first inside a `flex-col-reverse` container. The
 * flip lives here so no component ever re-derives it, and the `!= null` filter is
 * load-bearing: a page array can carry holes when a message is deleted mid-scroll.
 */

import { keepPreviousData } from "@tanstack/react-query";
import { useEffect } from "react";

import {
  reapLandedPendingSends,
  usePendingSends,
} from "../_hooks/use-pending-sends";
import { mergePendingMessages } from "../_libs/optimistic-message";
import {
  type ChannelMessage,
  useGetChannelsMessagesByCid,
} from "./channel-endpoints";

export const CHANNEL_MESSAGE_PAGE_SIZE = 25;

export type UseChannelMessagesOptions = {
  pageSize?: number;
  enabled?: boolean;
  /**
   * Drop thread replies from the conversation.
   *
   * `GET /:cid/messages` has no roots-only filter — an omitted `parentId` means "every
   * message", replies included — so a surface that renders replies in a separate thread
   * view (the office notes tab) would otherwise show each reply twice. The filter is
   * therefore applied to the loaded page, which means a page can render fewer than
   * `take` rows; it does not change how many the server counts.
   */
  rootsOnly?: boolean;
};

export function useChannelMessages(
  cid: string,
  {
    pageSize = CHANNEL_MESSAGE_PAGE_SIZE,
    enabled = true,
    rootsOnly = false,
  }: UseChannelMessagesOptions = {},
) {
  const result = useGetChannelsMessagesByCid({ cid })
    .query({ take: pageSize })
    .getInfinite({ enabled: enabled && !!cid });

  const fetched = (result.data?.pages.flatMap((page) => page.data) ?? [])
    .filter((message): message is ChannelMessage => message != null)
    .filter((message) => !rootsOnly || message.replyToId == null)
    .reverse();

  // Sends this browser has made but not yet read back. They are the newest thing in
  // the conversation, so they tail an oldest-first list.
  const pending = usePendingSends(cid);
  const messages = mergePendingMessages(fetched, pending);

  // The fetched list is the only thing that can say a send has landed, so it is what
  // retires the placeholder. Keyed on `dataUpdatedAt` — the refetch's fingerprint —
  // because `fetched` is a fresh array on every render and nothing can land without
  // new data.
  const { dataUpdatedAt } = result;
  useEffect(() => {
    reapLandedPendingSends(cid, fetched);
  }, [cid, dataUpdatedAt]);

  return { ...result, data: messages };
}

/**
 * The pinned strip.
 *
 * A separate query rather than a client-side filter of the thread: a pin can live on a
 * message hundreds of pages back, so filtering the loaded window would show the strip
 * emptying as the user scrolls.
 */
export function useChannelPinnedMessages(cid: string, enabled = true) {
  return useGetChannelsMessagesByCid({ cid })
    .query({ pinnedOnly: true, take: 50 })
    .get({ enabled: enabled && !!cid });
}

/** Below this, a term matches most of a thread and the round trip is wasted. */
export const CHANNEL_SEARCH_MIN_LENGTH = 2;

/** One page is the whole result set — the popover scrolls, it does not paginate. */
const CHANNEL_SEARCH_PAGE_SIZE = 30;

/**
 * Messages in ONE channel whose body matches `term`.
 *
 * The server does the matching (`ILIKE '%term%'` on `Message.body`, see
 * `channel-message.repository.ts`), newest first — which is the order a reader wants, so
 * nothing is reversed here the way {@link useChannelMessages} reverses the thread.
 *
 * ## Replies are NOT filtered out
 *
 * A match inside a comment reply-thread is the one result the reader cannot reach by
 * scrolling, so dropping it would remove the reason to search at all. The caller tells
 * the two apart by `replyToId` and routes the jump accordingly.
 *
 * ## `term` must already be debounced
 *
 * It is part of the query key. A key per keystroke is a request per keystroke.
 *
 * ## Why `enabled` is returned, not just applied
 *
 * `keepPreviousData` deliberately survives the query being switched off, so a cleared
 * input still holds the last term's rows in `data`. The caller must render results only
 * while `enabled` is true — the flag is part of this hook's answer, not an input to it.
 */
export function useChannelMessageSearch(cid: string, term: string) {
  const search = term.trim();
  const enabled = !!cid && search.length >= CHANNEL_SEARCH_MIN_LENGTH;

  const result = useGetChannelsMessagesByCid({ cid })
    .query({ search, take: CHANNEL_SEARCH_PAGE_SIZE })
    .get({ enabled, placeholderData: keepPreviousData });

  const messages = (result.data ?? []).filter(
    (message): message is ChannelMessage => message != null,
  );

  return { ...result, data: messages, enabled };
}

/**
 * One thread's replies.
 *
 * `parentId` is the `Message.id` of the root message.
 */
export function useChannelThreadReplies(
  cid: string,
  parentId: string | null,
  enabled = true,
) {
  const result = useGetChannelsMessagesByCid({ cid })
    .query({ parentId: parentId ?? undefined, take: CHANNEL_MESSAGE_PAGE_SIZE })
    .get({ enabled: enabled && !!cid && parentId != null });

  const replies = (result.data ?? [])
    .filter((message): message is ChannelMessage => message != null)
    .slice()
    .reverse();

  return { ...result, data: replies };
}
